import { deriveAvatarInitial } from '../../components/AvatarEditPopup';

/**
 * user_profiles 의 updated_avatar_url 이 비어있으면 닉네임 첫 글자를 fallback 으로.
 * 의도적으로 OAuth(user_metadata.avatar_url) 로 떨어지지 않음 — 사용자가 지운 썸네일이
 * 다시 OAuth 옛 사진으로 보이는 문제 회피.
 */
function pickAvatar(profileUrl, displayName) {
  if (typeof profileUrl === 'string' && profileUrl.trim()) return profileUrl;
  const initial = deriveAvatarInitial(displayName);
  if (initial && initial !== '?') return initial;
  // null 을 반환하면 SafeAvatar 가 PersonIcon SVG 로 폴백 처리
  return null;
}

/**
 * Supabase categories/tasks 중첩 응답 → 대시보드용 카테고리·멤버·초대 매핑
 * @param {object} params
 * @param {Array} params.categoriesWithTasks
 * @param {Array} params.membersData
 * @param {Array} params.invitesData
 * @param {object} params.baseRow 프로젝트 메타(날짜 등)
 * @param {object} params.user
 * @param {object} params.profile
 */
export function mapProjectDashboardDetail({
  categoriesWithTasks,
  membersData,
  invitesData,
  baseRow,
  user,
  profile,
  t: tr,
}) {
  const mappedCategories = (categoriesWithTasks || []).map((c) => {
    const sortedTasks = (c.tasks || []).sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    const mappedTasks = sortedTasks.map((t) => {
      const sortedChecklists = (t.checklists || []).sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );

      const checklist = sortedChecklists.map((item) => {
        const isMe = item.completed_by === user.id;
        const completerProfileUrl = isMe
          ? profile?.updated_avatar_url ?? item.user_profiles?.updated_avatar_url
          : item.user_profiles?.updated_avatar_url;
        const completerName =
          item.user_profiles?.updated_display_name ||
          (isMe
            ? profile?.updated_display_name || user?.user_metadata?.full_name
            : tr('common.noName'));
        return {
          id: item.id,
          text: item.name,
          hint: item.hint || null,
          createdBy: item.created_by,
          createdAt: item.created_at,
          completed: item.is_completed || false,
          completedAt: item.completed_at,
          completedBy: item.completed_by
            ? {
                id: item.completed_by,
                name: completerName,
                avatar: pickAvatar(completerProfileUrl, completerName),
              }
            : null,
        };
      });

      const taskAssignees = (t.task_assignees || []).map((a) => {
        const isMe = a.user_id === user.id;
        const assigneeProfileUrl = isMe
          ? profile?.updated_avatar_url ?? a.user_profiles?.updated_avatar_url
          : a.user_profiles?.updated_avatar_url;
        const assigneeName =
          a.user_profiles?.updated_display_name ||
          (isMe ? profile?.updated_display_name || user?.user_metadata?.full_name : tr('common.noName'));
        return {
          id: a.user_id,
          name: assigneeName,
          avatar: pickAvatar(assigneeProfileUrl, assigneeName),
        };
      });

      const sortedPosts = (t.posts || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      const posts = sortedPosts.map((post) => {
        const isPostMe = post.author_id === user.id;
        const postProfileUrl = isPostMe
          ? profile?.updated_avatar_url ?? post.user_profiles?.updated_avatar_url
          : post.user_profiles?.updated_avatar_url;
        const postAuthorName =
          post.user_profiles?.updated_display_name ||
          (isPostMe
            ? profile?.updated_display_name || user?.user_metadata?.full_name
            : tr('common.noName'));
        const postLikeUserIds = Array.isArray(post.post_likes)
          ? post.post_likes.map((l) => l.user_id)
          : [];

        // 댓글을 일단 평면 매핑 (parent 메타 채우기 전 1차 패스)
        const flatComments = (post.comments || []).map((comment) => {
          const isCommentMe = comment.author_id === user.id;
          const cmtProfileUrl = isCommentMe
            ? profile?.updated_avatar_url ?? comment.user_profiles?.updated_avatar_url
            : comment.user_profiles?.updated_avatar_url;
          const cmtAuthorName =
            comment.user_profiles?.updated_display_name ||
            (isCommentMe
              ? profile?.updated_display_name || user?.user_metadata?.full_name
              : tr('common.noName'));
          const cmtLikeUserIds = Array.isArray(comment.comment_likes)
            ? comment.comment_likes.map((l) => l.user_id)
            : [];
          return {
            id: comment.id,
            authorId: comment.author_id,
            author: cmtAuthorName,
            avatar: pickAvatar(cmtProfileUrl, cmtAuthorName),
            content: comment.content,
            createdAt: comment.created_at,
            attachments: Array.isArray(comment.attachments) ? comment.attachments : [],
            hiddenAt: comment.hidden_at || null,
            hiddenBy: comment.hidden_by || null,
            parentCommentId: comment.parent_comment_id || null,
            likeUserIds: cmtLikeUserIds,
            likeCount: cmtLikeUserIds.length,
            likedByMe: cmtLikeUserIds.includes(user.id),
          };
        });

        // 댓글 id → 댓글 lookup 으로 parentAuthor / parentSnippet 채움.
        // 부모가 가려진 상태면 본문 노출하지 않음(가려졌다는 사실만 표시).
        const byId = new Map(flatComments.map((c) => [c.id, c]));
        flatComments.forEach((c) => {
          if (c.parentCommentId) {
            const parent = byId.get(c.parentCommentId);
            if (parent) {
              c.parentAuthor = parent.author;
              c.parentAuthorId = parent.authorId;
              c.parentHiddenAt = parent.hiddenAt;
              c.parentSnippet = parent.hiddenAt ? '' : parent.content || '';
            } else {
              c.parentAuthor = tr('common.deletedComment');
              c.parentHiddenAt = null;
              c.parentSnippet = '';
            }
          }
        });

        // 각 댓글의 자식 수(direct reply 수) 계산
        const replyCountById = new Map();
        flatComments.forEach((c) => {
          if (c.parentCommentId) {
            replyCountById.set(
              c.parentCommentId,
              (replyCountById.get(c.parentCommentId) || 0) + 1
            );
          }
        });
        flatComments.forEach((c) => {
          c.replyCount = replyCountById.get(c.id) || 0;
        });

        // 시간 오름차순(과거 → 현재) — 답글이 부모 뒤에 자연스럽게 나오도록.
        // 부모/자식 관계 자체는 parent_comment_id 로 명시되어 있어 정렬과 무관.
        const sortedComments = flatComments.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

        return {
          id: post.id,
          authorId: post.author_id,
          author: postAuthorName,
          avatar: pickAvatar(postProfileUrl, postAuthorName),
          content: post.content,
          createdAt: post.created_at,
          attachments: Array.isArray(post.attachments) ? post.attachments : [],
          hiddenAt: post.hidden_at || null,
          hiddenBy: post.hidden_by || null,
          likeUserIds: postLikeUserIds,
          likeCount: postLikeUserIds.length,
          likedByMe: postLikeUserIds.includes(user.id),
          comments: sortedComments,
        };
      });

      const completedCount = checklist.filter((item) => item.completed).length;
      const progress =
        checklist.length > 0 ? Math.round((completedCount / checklist.length) * 100) : 100;

      return {
        id: t.id,
        name: t.name,
        createdBy: t.created_by,
        startDate: new Date(t.start_date),
        endDate: new Date(t.end_date),
        progress,
        assignees: taskAssignees,
        checklist,
        posts,
        description: t.description || '',
      };
    });

    return {
      id: c.id,
      name: c.name,
      createdBy: c.created_by,
      sort_order: c.sort_order,
      status: '대기',
      startDate: baseRow.startDate ?? new Date(),
      endDate: baseRow.endDate ?? new Date(),
      progress:
        mappedTasks.length > 0
          ? Math.round(
              mappedTasks.reduce((acc, task) => acc + task.progress, 0) / mappedTasks.length
            )
          : 0,
      assignees: [],
      tasks: mappedTasks,
    };
  });

  const mappedMembers = (membersData || []).map((m) => ({
    id: m.user_id,
    name: m.user_profiles?.updated_display_name,
    role: m.role,
    avatar: pickAvatar(
      m.user_profiles?.updated_avatar_url,
      m.user_profiles?.updated_display_name
    ),
    email: m.user_profiles?.email,
    joined_at: m.joined_at,
  }));

  const mappedInvites = (invitesData || []).map((inv) => ({
    id: inv.email,
    email: inv.email,
    status: 'pending',
    lastSent: new Date(inv.invited_at).getTime(),
  }));

  return { mappedCategories, mappedMembers, mappedInvites };
}
